// Where the first sign-in after signup lands (#428).
//
// Signup used to log the user in and navigate straight to the next screen.
// Accounts now stay unconfirmed until the emailed link is clicked, which opens
// a new tab, so that navigation is carried in two places instead:
//   - the account: user_metadata.post_verify_redirect, set by
//     create-trial-account (the only writer), so it survives any device;
//   - this browser: the quick-quote draft, which is too large for metadata
//     (metadata rides in every JWT) and was in per-tab sessionStorage.

export const POST_VERIFY_NEW_ESTIMATE = 'new_estimate'
export const NEW_ESTIMATE_PATH = '/estimates?new=1'

const QUICKQUOTE_DRAFT_KEY = 'prosperia.quickquoteDraft'
const QUICKQUOTE_SESSION_KEYS = ['aiInitialService', 'quickquoteFormData']
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

export const getPostSignInPath = (user) =>
  user?.user_metadata?.post_verify_redirect === POST_VERIFY_NEW_ESTIMATE ? NEW_ESTIMATE_PATH : '/dashboard'

/** At signup: copy the quick-quote draft out of this tab so the verify tab can use it. */
export const persistQuickQuoteDraft = (now = Date.now()) => {
  try {
    const draft = {}
    QUICKQUOTE_SESSION_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(key)
      if (value) draft[key] = value
    })
    if (Object.keys(draft).length === 0) return
    localStorage.setItem(QUICKQUOTE_DRAFT_KEY, JSON.stringify({ savedAt: now, draft }))
  } catch (err) {
    console.warn('[postSignIn] Could not save quick-quote draft:', err)
  }
}

/**
 * On the new-estimate screen: put a fresh draft back where Estimates reads it.
 * Always removes the stored copy, so it is used at most once.
 */
export const restoreQuickQuoteDraft = (now = Date.now()) => {
  try {
    const raw = localStorage.getItem(QUICKQUOTE_DRAFT_KEY)
    if (!raw) return
    localStorage.removeItem(QUICKQUOTE_DRAFT_KEY)
    const { savedAt, draft } = JSON.parse(raw)
    if (!draft || !(now - savedAt < DRAFT_TTL_MS)) return
    QUICKQUOTE_SESSION_KEYS.forEach((key) => {
      if (typeof draft[key] === 'string' && !sessionStorage.getItem(key)) sessionStorage.setItem(key, draft[key])
    })
  } catch (err) {
    console.warn('[postSignIn] Could not restore quick-quote draft:', err)
  }
}

/** Clear the account flag once it has been acted on, so later sign-ins go to the dashboard. */
export const clearPostVerifyRedirect = async (supabase, user) => {
  if (!user?.user_metadata?.post_verify_redirect) return
  const { error } = await supabase.auth.updateUser({ data: { post_verify_redirect: null } })
  if (error) console.warn('[postSignIn] Could not clear post_verify_redirect:', error.message)
}
